import * as React from 'react';
import { FC } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import spacing from 'src/constants/spacing';
import mediaQueries from 'src/constants/responsive';
import stylesheet, { backgroundColor } from 'src/constants/stylesheet';
import Panels from './Panels';
import { useApi } from 'src/@context/Api';
import { Link } from 'react-router-dom';
import Image from './Image';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            maxWidth: spacing.ContentMaxWidt,
            margin: '0 auto',
            boxSizing: 'content-box',
            [mediaQueries.Mobile]: {
                padding: '0 20px',
            },
            [mediaQueries.AfterMobile]: {
                padding: '0 60px',
            }
        },
        header: {
            padding: '2rem 0 1rem 0'
        },
        title: {
            ...stylesheet.TitleH1,
            textAlign: 'center',
            marginBottom: '.2em'
        },
        subTitle: {
            textAlign: 'center',
        },
        content: {
            position: 'relative',
            [mediaQueries.Mobile]: {},
            [mediaQueries.AfterMobile]: {
                display: 'flex',
                flexWrap: 'wrap',
            },
            [mediaQueries.Tablet]: {
                marginLeft: -spacing.Gutter.Tablet / 2,
                marginRight: -spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                marginLeft: -spacing.Gutter.Laptop / 2,
                marginRight: -spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                marginLeft: -spacing.Gutter.Desktop / 2,
                marginRight: -spacing.Gutter.Desktop / 2,
            },
        },
        panels: {
            boxSizing: 'border-box',
            [mediaQueries.Mobile]: {
                width: `100%`,
                marginBottom: spacing.Gutter.Mobile * 2,
            },
            [mediaQueries.AfterMobile]: {
                width: `${2 / 3 * 100}%`,
            },
            [mediaQueries.Tablet]: {
            },
            [mediaQueries.Laptop]: {
            },
            [mediaQueries.Desktop]: {
            },
        },
        hr: {
            ...stylesheet.HR,
            // marginBottom: 13,
            [mediaQueries.Tablet]: {
                marginLeft: spacing.Gutter.Tablet / 2,
                marginRight: spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                marginLeft: spacing.Gutter.Laptop / 2,
                marginRight: spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                marginLeft: spacing.Gutter.Desktop / 2,
                marginRight: spacing.Gutter.Desktop / 2,
            },
        },
        asideHr: {
            ...stylesheet.HR,
            marginBottom: 0,
        },
        productInfo: {
            position: 'sticky',
            top: 0,
            boxSizing: 'border-box',
            marginBottom: 'auto',
            [mediaQueries.Mobile]: {
                width: `100%`,
                marginBottom: spacing.Gutter.Mobile * 2,
            },
            [mediaQueries.AfterMobile]: {
                width: `${1 / 3 * 100}%`,
            },
            [mediaQueries.Tablet]: {
                paddingLeft: spacing.Gutter.Tablet / 2,
                paddingRight: spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                paddingLeft: spacing.Gutter.Laptop / 2,
                paddingRight: spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                paddingLeft: spacing.Gutter.Desktop / 2,
                paddingRight: spacing.Gutter.Desktop / 2,
            },
        },
        productInfoText: {
            position: 'relative',
            backgroundColor,
            [mediaQueries.Mobile]: {
                padding: spacing.Gutter.Mobile,
                marginBottom: spacing.Gutter.Mobile,
            },
            [mediaQueries.Tablet]: {
                padding: spacing.Gutter.Tablet,
                marginBottom: spacing.Gutter.Tablet,
            },
            [mediaQueries.Laptop]: {
                padding: spacing.Gutter.Laptop,
                marginBottom: spacing.Gutter.Laptop,
            },
            [mediaQueries.Desktop]: {
                padding: spacing.Gutter.Desktop,
                marginBottom: spacing.Gutter.Desktop,
            },
        },
        infoBoxTitle: {
            ...stylesheet.TitleH2,
            paddingBottom: 15
        },
        labelsContainer: {
            [mediaQueries.Mobile]: {
                marginBottom: spacing.Gutter.Mobile,
            },
            [mediaQueries.Tablet]: {
                marginBottom: spacing.Gutter.Tablet,
            },
            [mediaQueries.Laptop]: {
                marginBottom: spacing.Gutter.Laptop,
            },
            [mediaQueries.Desktop]: {
                marginBottom: spacing.Gutter.Desktop,
            },
        },
        label: {
            boxSizing: 'border-box',
            width: `100%`,
            [mediaQueries.Mobile]: {
                marginBottom: spacing.Gutter.Mobile,
            },
            [mediaQueries.Tablet]: {
                marginBottom: spacing.Gutter.Tablet,
            },
            [mediaQueries.Laptop]: {
                marginBottom: spacing.Gutter.Laptop,
            },
            [mediaQueries.Desktop]: {
                marginBottom: spacing.Gutter.Desktop,
            },
            '&:last-child': {
                marginBottom: 0,
            }
        },
        figure: {
            position: 'relative',
            height: 0,
            overflow: 'hidden',
            paddingBottom: '50%',
            background: backgroundColor
        },
        img: {
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            padding: '10px 40px',
            ...stylesheet.ObjectFitContain
        },
        link: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1
        }
    }));

export type ProductProps = ProductDto;
type Props = ProductProps;

const Product: FC<Props> = ({ Title, Panels: panels, ProductInfo, SubTitle, Labels }) => {
    const cls = useStyles();
    const header = document.getElementById('header');
    const headerHeight = header ? header.clientHeight + 50 : 0;

    const { app } = useApi();
    const config = app && app.Config;
    const translationsItems = config && config.TranslationItems;
    const infoBoxTitle = translationsItems && translationsItems.find(t => t.UID === 'infobox_title');

    const sizes = '(max-width:1024px) 30vw, (min-width: 1025px) 300px';

    return (
        <section className={cls.root}>
            <header className={cls.header}>
                {Title && <h1 className={cls.title}>{Title}</h1>}
                {SubTitle && <h5 className={cls.subTitle}>{SubTitle}</h5>}
            </header>
            <div className={cls.content}>
                <article className={cls.panels}>
                    <hr className={cls.hr} />
                    <Panels panels={panels} isProductPage={true} />
                </article>

                <aside className={cls.productInfo} style={{ top: headerHeight }}>
                    <hr className={cls.asideHr} />
                    <div className={cls.productInfoText}>
                        <h2 className={cls.infoBoxTitle}>{infoBoxTitle && infoBoxTitle.Translation || ''}</h2>
                        <div dangerouslySetInnerHTML={{ __html: ProductInfo }} />
                    </div>
                    <div className={cls.labelsContainer}>
                        {
                            Labels.map((l, i) => {
                                const isInternal = l.Link.length > 0 && l.Link.startsWith('/');

                                return (
                                    <div
                                        key={`label-${i}`}
                                        className={cls.label}
                                    >
                                        <div className={cls.figure}>
                                            {l.Link && isInternal &&
                                                <Link to={l.Link} className={cls.link} />}
                                            {l.Link && !isInternal &&
                                                <a href={l.Link} target='_blank' className={cls.link} />}
                                            <Image pictureRef={l.Image} sizes={sizes} className={cls.img} />
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </aside>
            </div>
        </section>
    );
}

export default Product;


// WEBPACK FOOTER //
// ./src/Components/Product.tsx